import { useEffect, useState } from 'react'

/**
 * Keeps a node mounted for `durationMs` after it is asked to close, so an exit
 * animation can play.
 *
 * React removes a conditionally rendered element the instant its condition
 * flips, which is why the Quasar overlays' replacements would otherwise vanish
 * rather than settle. `isClosing` drives the `is-closing` class that reverses
 * the entrance animation.
 */
export function useExitTransition(isOpen: boolean, durationMs = 220) {
  const [isMounted, setIsMounted] = useState(isOpen)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
      setIsClosing(false)
      return
    }

    if (!isMounted) return

    setIsClosing(true)
    const id = window.setTimeout(() => {
      setIsMounted(false)
      setIsClosing(false)
    }, durationMs)
    return () => window.clearTimeout(id)
  }, [isOpen, isMounted, durationMs])

  return { isMounted, isClosing } as const
}
